/**
 * Sentry error tracking initialization for the admin portal.
 *
 * This module sets up Sentry for React with:
 * - Error boundary integration
 * - Performance monitoring
 * - Session replay (optional, for debugging)
 * - React Router integration
 */

import * as Sentry from '@sentry/react'

// Only initialize in production/staging
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || ''
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development'
const IS_PRODUCTION = ENVIRONMENT === 'production' || ENVIRONMENT === 'staging'

export function initSentry(): void {
  if (!SENTRY_DSN || !IS_PRODUCTION) {
    console.log('[Sentry] Skipping initialization (no DSN or development mode)')
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    // Performance Monitoring
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in staging

    // Session Replay (only in staging for debugging)
    replaysSessionSampleRate: ENVIRONMENT === 'staging' ? 0.1 : 0,
    replaysOnErrorSampleRate: ENVIRONMENT === 'staging' ? 1.0 : 0,

    // Integration configuration
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out common non-errors
    beforeSend(event, _hint) {
      // Ignore ResizeObserver errors (browser noise)
      if (event.message?.includes('ResizeObserver')) {
        return null
      }

      // Ignore network errors that are expected
      if (event.message?.includes('Network request failed')) {
        return null
      }

      // Ignore canceled requests
      if (event.message?.includes('canceled')) {
        return null
      }

      return event
    },

    // Scrub sensitive data
    beforeSendTransaction(event) {
      // Remove any PII from transaction data
      if (event.contexts?.trace?.data) {
        delete event.contexts.trace.data
      }
      return event
    },
  })

  console.log(`[Sentry] Initialized for ${ENVIRONMENT}`)
}

/**
 * Set user context for error tracking.
 * Call this after successful authentication.
 */
export function setSentryUser(user: {
  id: string
  email?: string
  role_labels?: string[]
  tenantId?: string
}): void {
  if (!IS_PRODUCTION) return

  Sentry.setUser({
    id: user.id,
    // Don't include email in production for privacy
    ...(ENVIRONMENT !== 'production' && { email: user.email }),
  })

  // Set tenant context as a tag
  if (user.tenantId) {
    Sentry.setTag('tenant_id', user.tenantId)
  }

  if (user.role_labels?.length) {
    Sentry.setTag('user_roles', user.role_labels.join(', '))
  }
}

/**
 * Clear user context on logout.
 */
export function clearSentryUser(): void {
  Sentry.setUser(null)
  Sentry.setTag('tenant_id', undefined)
  Sentry.setTag('user_role', undefined)
}

/**
 * Capture an exception with additional context.
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  if (!IS_PRODUCTION) {
    console.error('[Sentry] Would capture:', error, context)
    return
  }

  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context)
    }
    Sentry.captureException(error)
  })
}

/**
 * Capture a message for logging.
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
): void {
  if (!IS_PRODUCTION) {
    console.log(`[Sentry] Would log (${level}):`, message, context)
    return
  }

  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context)
    }
    Sentry.captureMessage(message, level)
  })
}

/**
 * Add breadcrumb for debugging context.
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
): void {
  if (!IS_PRODUCTION) return

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  })
}

// Export Sentry ErrorBoundary for use in components
export const ErrorBoundary = Sentry.ErrorBoundary
